import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SKI_PLACEHOLDER_IMAGE } from '../lib/placeholder';

export default function QuiverList() {
  const navigate = useNavigate();
  const [skis, setSkis] = useState([]);
  const [reviewCounts, setReviewCounts] = useState({}); // { ski_id: count }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const skisPromise = supabase
        .from('skis')
        .select('id, brand, model, year, category, waist_width_mm, turn_radius_m, image_url, playful_to_carve')
        .order('brand', { ascending: true })
        .order('model', { ascending: true });

      // Pull just (id, ski_id) to count per-ski. Tiny payload at our scale.
      const notesPromise = supabase
        .from('ski_notes')
        .select('ski_id');

      const [skisRes, notesRes] = await Promise.all([skisPromise, notesPromise]);
      if (cancelled) return;

      if (skisRes.error) { setError(skisRes.error.message); setLoading(false); return; }
      if (notesRes.error) { setError(notesRes.error.message); setLoading(false); return; }

      const counts = {};
      for (const row of (notesRes.data || [])) {
        counts[row.ski_id] = (counts[row.ski_id] || 0) + 1;
      }
      setSkis(skisRes.data || []);
      setReviewCounts(counts);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return skis;
    return skis.filter((s) => {
      const haystack = `${s.brand} ${s.model} ${s.category || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [skis, search]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>The Quiver</h1>
        <p style={styles.subtitle}>
          Real reviews from real skiers — not pros, not sponsored, just notes.
        </p>
      </div>

      <div style={styles.searchWrap}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by brand, model, or category…"
          style={styles.searchInput}
        />
      </div>

      {loading && <div style={styles.message}>Loading skis…</div>}
      {error && <div style={styles.error}>Failed to load: {error}</div>}
      {!loading && !error && filtered.length === 0 && (
        <div style={styles.message}>No skis match that search.</div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div style={styles.tableCard}>
          <div style={styles.tableScroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.thImage} aria-label="Image" />
                  <th style={styles.th}>Brand</th>
                  <th style={styles.th}>Model</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.thNum}>Waist</th>
                  <th style={styles.thNum}>Radius</th>
                  <th style={styles.th}>Playful ↔ Carve</th>
                  <th style={styles.thNum}>Reviews</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const isHovered = hoveredId === s.id;
                  return (
                    <tr
                      key={s.id}
                      onClick={() => navigate(`/quiver/${s.id}`)}
                      onMouseEnter={() => setHoveredId(s.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      style={{
                        ...styles.tr,
                        ...(isHovered ? styles.trHover : null),
                      }}
                    >
                      <td style={styles.tdImage}>
                        <SkiThumb url={s.image_url} alt={`${s.brand} ${s.model}`} />
                      </td>
                      <td style={styles.tdBrand}>{s.brand}</td>
                      <td style={styles.tdModel}>{s.model}</td>
                      <td style={styles.td}>{s.category || '—'}</td>
                      <td style={styles.tdNum}>{s.waist_width_mm != null ? `${s.waist_width_mm}mm` : '—'}</td>
                      <td style={styles.tdNum}>{s.turn_radius_m != null ? `${s.turn_radius_m}m` : '—'}</td>
                      <td style={styles.tdSlider}>
                        <MiniSlider position={s.playful_to_carve} />
                      </td>
                      <td style={styles.tdNum}>{reviewCounts[s.id] || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SkiThumb({ url, alt }) {
  return <img src={url || SKI_PLACEHOLDER_IMAGE} alt={alt} style={styles.thumb} />;
}

function MiniSlider({ position }) {
  if (position == null) {
    return <div style={styles.miniSliderEmpty}>—</div>;
  }
  const clamped = Math.max(0, Math.min(1, Number(position)));
  return (
    <div style={styles.miniSliderWrap}>
      <div style={styles.miniSliderTrack}>
        <div style={{ ...styles.miniSliderDot, left: `${clamped * 100}%` }} />
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: 1200, margin: '0 auto', padding: '20px 20px 60px', fontFamily: 'Inter, system-ui, sans-serif' },
  header: { textAlign: 'center', marginBottom: 32, marginTop: 12 },
  title: { fontSize: '2.5rem', fontWeight: 800, color: '#1E293B', margin: 0, letterSpacing: '-0.025em' },
  subtitle: { fontSize: '1.05rem', color: '#64748B', margin: '8px 0 0' },
  searchWrap: { maxWidth: 560, margin: '0 auto 32px' },
  searchInput: {
    width: '100%',
    padding: '14px 18px',
    borderRadius: 14,
    border: '2px solid #E2E8F0',
    fontSize: '1rem',
    color: '#1E293B',
    background: 'white',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },

  tableCard: {
    background: 'white',
    borderRadius: 20,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
  },
  tableScroll: { overflowX: 'auto' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.92rem',
  },
  th: {
    textAlign: 'left',
    padding: '16px 20px',
    background: '#F8FAFC',
    color: '#64748B',
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E2E8F0',
    whiteSpace: 'nowrap',
  },
  thNum: {
    textAlign: 'right',
    padding: '16px 20px',
    background: '#F8FAFC',
    color: '#64748B',
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E2E8F0',
    whiteSpace: 'nowrap',
  },
  thImage: {
    padding: '16px 12px 16px 20px',
    background: '#F8FAFC',
    borderBottom: '1px solid #E2E8F0',
    width: 76,
  },
  tr: {
    borderBottom: '1px solid #F1F5F9',
    cursor: 'pointer',
    transition: 'background-color 0.12s ease',
  },
  trHover: {
    backgroundColor: '#FAF5FF',
  },
  td: { padding: '12px 20px', color: '#475569', whiteSpace: 'nowrap' },
  tdNum: { padding: '12px 20px', color: '#475569', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  tdImage: { padding: '12px 12px 12px 20px', verticalAlign: 'middle' },
  tdBrand: {
    padding: '12px 20px',
    color: '#7C3AED',
    fontWeight: 700,
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  tdModel: {
    padding: '12px 20px',
    color: '#1E293B',
    fontWeight: 700,
    fontSize: '0.98rem',
    whiteSpace: 'nowrap',
  },
  tdSlider: { padding: '12px 20px', verticalAlign: 'middle' },

  thumb: {
    display: 'block',
    width: 56,
    height: 36,
    objectFit: 'cover',
    borderRadius: 8,
    background: '#F8FAFC',
  },

  miniSliderWrap: { width: 130 },
  miniSliderTrack: {
    position: 'relative',
    height: 8,
    background: 'linear-gradient(to right, #DBEAFE, #EDE9FE)',
    borderRadius: 999,
  },
  miniSliderDot: {
    position: 'absolute',
    top: '50%',
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: '#7C3AED',
    border: '2px solid white',
    boxShadow: '0 1px 4px rgba(124,58,237,0.4)',
    transform: 'translate(-50%, -50%)',
  },
  miniSliderEmpty: {
    width: 130,
    color: '#CBD5E1',
    fontSize: '0.85rem',
  },

  message: { textAlign: 'center', padding: 40, color: '#64748B' },
  error: { textAlign: 'center', padding: 40, color: '#DC2626' },
};
